import React, {
  ReactNode, useEffect, useLayoutEffect, useState,
} from 'react';
import { Popover } from 'antd';
import { getEnv, navigate, pageAgent } from '@/common/agents';
import preQrCode from '@/assets/pre-qrcode.png';
import betaQrCode from '@/assets/beta-qrcode.png';
import relaseQrCode from '@/assets/release-qrcode.png';

import RoboneoContext from '$/pages/RoboneoContext';
import CalcRadish from '$/components/CalcRadish';
import IconFont from '$/components/IconFont';
import { message } from '$/components/Message';
import { InitLoginConfig, useOfficialStore } from '$/store/useOfficialStore';
import useThemeStore, { setTheme } from '$/store/useThemeStore';
import { isMobile } from '$/utils';
import { NoSSR } from '@meitu/catalyze-next';
import UserCard from '@/components/UserCard';
import CookieBanner from '@/components/CookieBanner';
import { useCommonStore } from '@/store/useCommonStore';
import type { OverseaFeatureItem } from '@/apiSdk/api.type';
// import ProductOperation from '@/components/ProductOperation';
import SeoHeader from '@/components/SeoHeader';
import OverseaOfficialHeader, { type OverseaOfficialHeaderAppearance } from '@/components/overseaHome/layout/header';

import styles from './index.module.scss';

type LandingHeaderVariant = 'seo' | 'overseaOfficial';

/** 海外官网 Header 依赖服务端菜单数据；为空时组件仍展示 Logo、Pricing、登录等基础入口。 */
type OverseaOfficialHeaderMenus = Record<'product' | 'blog', OverseaFeatureItem[]>;

/**
 * 落地页顶栏右侧：下载 / Discord、UserCard、萝卜；默认隐藏 UserCard 内「系统设置」以免与主站 Header 重复。
 */
function UserCardWithVip({
  isClient,
  isOversea,
  joinDiscord,
  downloadContent,
  openQrCode,
  showSystemSetting,
}: {
  isClient: boolean;
  isOversea: boolean;
  joinDiscord: () => void;
  downloadContent: () => React.ReactNode;
  openQrCode: (open: boolean) => void;
  /** Seedance 等落地页需与主站一致：顶栏头像可进系统设置切换语言（含主题、水印等） */
  showSystemSetting?: boolean;
}) {
  if (!isClient) return null;

  return (
    <div className={styles.use_card_box} style={{ position: 'relative', top: '0', right: '0' }}>
      <div className={styles['product-card-box']}>
        {/* <div className={styles['product-item']}>
          <ProductOperation />
        </div> */}
        {isOversea && (
          <div className={styles['product-item']} onClick={joinDiscord}>
            <div
              style={{
                width: '40px',
                display: 'flex',
                alignContent: 'center',
                justifyContent: 'center',
              }}
            >
              <IconFont
                className={styles['product-item-icon']}
                fontSize={16}
                name="discord"
              />
            </div>
          </div>
        )}
        <div className={styles['product-item']}>
          <Popover
            placement="bottom"
            trigger={['hover']}
            overlayStyle={{
              zIndex: 'calc(var(--zindex-fixed) + 1) !important',
            }}
            overlayClassName="resetPopoverAndArrow"
            content={downloadContent()}
            onOpenChange={openQrCode}
          >
            <div className={styles.downloadBox} style={{ height: '100%' }}>
              <IconFont
                className={styles['product-item-icon']}
                fontSize={16}
                name="mobilePhoneV2Bold"
              />
            </div>
          </Popover>
        </div>
      </div>
      <UserCard options={{ hiddenSystemSetting: !showSystemSetting }} />
      <CalcRadish />
    </div>
  );
}

/**
 * 落地页布局容器：SEO 顶栏、主题、登录与 RoboneoContext。
 * @param headerFixedTop 控制 SEO 顶栏是否 position:fixed，默认 true。
 * @param headerVariant 国内 Essio 页面走轻量 SEO Header，海外 Essio 页面复用官网 Header。
 * @param overseaHeaderAppearance 控制海外官网 Header 首屏透明或带遮罩，默认保持官网首页透明样式。
 */
function FeatureContext({
  children,
  hideHeader,
  v0Thumail,
  landingHeaderShowUserCardSystemSetting = false,
  headerFixedTop = true,
  headerVariant = 'seo',
  overseaHeaderAppearance = 'transparent',
  overseaHeaderMenus = { product: [], blog: [] },
}: {
  children: ReactNode;
  hideHeader: boolean;
  v0Thumail: boolean;
  /** 为 true 时顶栏 UserCard 展示「系统设置」（内语言切换等与主站 Header 一致） */
  landingHeaderShowUserCardSystemSetting?: boolean;
  /** 为 false 时 SEO 顶栏不 fixed，随文档流滚动（如 Seedance 页） */
  headerFixedTop?: boolean;
  /** Essio 海外落地页复用官网 Header，国内默认继续走 SEO Header */
  headerVariant?: LandingHeaderVariant;
  /** 海外官网 Header 首屏外观：官网首页默认透明，白底 PSEO 页可强制遮罩保证可读性 */
  overseaHeaderAppearance?: OverseaOfficialHeaderAppearance;
  /** 海外官网 Header 的 Product/Blog 菜单数据 */
  overseaHeaderMenus?: OverseaOfficialHeaderMenus;
}) {
  const isOversea = useOfficialStore((state) => state.isOversea());
  // const languageStore = useLanguageStore((state) => state.languageStore);
  const toLogin = useCommonStore((state) => state.toLogin);
  const initUserInfo = useCommonStore((state) => state.initUserInfo);
  const init = useCommonStore((state) => state.init);
  const initialized = useCommonStore((state) => state.initialized);
  // const disposeOfflineListener = useCommonStore(
  //   (state) => state.disposeOfflineListener,
  // );
  const isUserLoggedIn = useOfficialStore((state) => state.isUserLoggedIn());
  const { locationName } = pageAgent.getCurrentPage() || {};
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  /**
   * 落地頁固定使用深色主題（與 body / html token 一致）；離開頁面時還原進入前的主題，避免影響站內其他路由。
   */
  useLayoutEffect(() => {
    const previousTheme = useThemeStore.getState().theme;
    setTheme('dark');
    return () => {
      setTheme(previousTheme);
    };
  }, []);

  const loadVconsole = async () => {
    if (isMobile() && process.env.NEXT_PUBLIC_ENVIRONMENT !== 'release') {
      const VConsole = await import('vconsole');
      // eslint-disable-next-line no-new, new-cap
      new VConsole.default();
    }
  };

  useEffect(() => {
    InitLoginConfig({
      toLogin: async () => {
        await toLogin();
        initUserInfo();
      },
    });
    init();
    loadVconsole();
  }, []);
  // 注册chat的参数

  useEffect(() => {
    if (
      initialized
      && !isUserLoggedIn
      && locationName
      && (locationName === 'studio' || locationName === 'ai_flow')
    ) {
      message.info('RoboNeo_Web_login_check_text');
      navigate({
        mode: 'url',
        url: getEnv().ENV === 'development' ? '/--roboneo/home' : '/home',
        replace: true,
      });
    }
  }, [locationName, initialized, isUserLoggedIn]);

  // useEffect(() => {
  //   process.env.NEXT_PUBLIC_SENTRY_AUTH_TOKEN
  //     && Sentry.init({
  //       dsn: 'https://b6e0e56681874ef08814fe61c2855c74@sentry.meitu-int.com/227',
  //       environment: process.env.NEXT_PUBLIC_ENVIRONMENT,
  //       integrations: [],
  //       normalizeDepth: 10, // 增加对象解析深度
  //       maxValueLength: 5000, // 字段最大长度
  //       maxBreadcrumbs: 100, // 面包屑数量限制
  //       tracesSampleRate: 1.0,
  //     });
  //   // 组件卸载时调用 disposeOfflineListener
  //   return () => {
  //     disposeOfflineListener();
  //   };
  // }, []);

  const openQrCode = (open: boolean) => {
    if (open) {
      window.mtstat?.track('download_mobile_hover', {});
    }
  };

  const joinDiscord = () => {
    window.open(` ${v0Thumail ? 'https://discord.gg/3czcMZpgbu' : 'https://discord.gg/JXt2gwDpVp'}`, '_blank');
  };

  const downloadContent = () => {
    const QR_CODE = {
      development: preQrCode,
      dev: preQrCode,
      pre: preQrCode,
      beta: betaQrCode,
      release: relaseQrCode,
    }[getEnv().ENV];
    return (
      <div className={styles.downloadPopverContent}>
        <div className={styles.qrcodeBox}>
          <img className={styles.qrcode} src={QR_CODE.src} alt="" />
        </div>
      </div>
    );
  };

  return (
    <>
      {/* 海外 Essio/PSEO 页面需与 roboneo.com 官网保持一致的导航与登录入口。 */}
      {!hideHeader && headerVariant === 'overseaOfficial' && (
        <OverseaOfficialHeader menus={overseaHeaderMenus} appearance={overseaHeaderAppearance} />
      )}
      {/* 国内落地页保留现有 SEO Header，避免影响已有投放页版式与占位策略。 */}
      {!hideHeader && headerVariant === 'seo' && (
        <SeoHeader
          fixedTop={headerFixedTop}
          // eslint-disable-next-line react/no-unstable-nested-components
          userCardWithVip={() => (
            <UserCardWithVip
              isClient={isClient}
              isOversea={isOversea}
              joinDiscord={joinDiscord}
              downloadContent={downloadContent}
              openQrCode={openQrCode}
              showSystemSetting={landingHeaderShowUserCardSystemSetting}
            />
          )}
        />
      )}
      <NoSSR>
        {isOversea ? <CookieBanner /> : null}
        <div
          id="layout_container"
          className={styles.layout_container}
        // data-roboneo-theme={theme}
          style={{ background: 'var(--background-canvas)' }}
        >
          <div className={styles['landingpage-container']}>

            <RoboneoContext
              enableVip
              vipLevelNumber={v0Thumail ? 2 : 'all'}
              disableRadishConfig={!!v0Thumail}
            >
              {children}
            </RoboneoContext>
          </div>
        </div>
      </NoSSR>
    </>
  );
}
export default FeatureContext;
